import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, Stack, Tabs } from "@mantine/core";
import {
  IconUser,
  IconCreditCard,
  IconStethoscope,
  IconFileText,
  IconClipboardList,
  IconClock,
  IconNote,
  IconFlask,
  IconReportMedical,
  IconPackage,
  IconGift,
} from "@tabler/icons-react";
import { useClient } from "../../context/ClientContext";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { ActivityEvent } from "../../types/clientActivity";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { useNavigate } from "react-router-dom";
import { useOptionalMessaging } from "../../context/MessagingContext";
import { isMessagingEnabled } from "../../utils/messagingFlag";
import { toE164 } from "../../utils/phoneUtils";
import usePermissions from "../../hooks/usePermissions";
import ClientForm from "./ClientForm";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import ActivityTimeline from "./ActivityTimeline";
import TransactionDetailModal from "../calendar/CalendarView/components/TransactionDetailModal/TransactionDetailModal";
import { GetTransactionsResponse } from "../../types/transactions";
import PaymentMethodsSection from "./PaymentMethodsSection";
import ClientPackagesSection from "./ClientPackagesSection";
import ClientGiftCardsSection from "./ClientGiftCardsSection";
import ClientProfileHeader from "./ClientProfileHeader";
import ClientOverviewPanel from "./ClientOverviewPanel";
import EditPointsModal from "./EditPointsModal";
import useLoyaltyProgram from "../../hooks/useLoyaltyProgram";
import { ClientLoyaltyResponse } from "../../types/loyalty";
import ClientNotesPanel from "./ClientNotesPanel";
import ClientDocumentsTab from "./ClientDocumentsTab";
import ClientFormsWaiversTab from "./ClientFormsWaiversTab";
import ClientSoapNotesTab from "./ClientSoapNotesTab";
import MedicalChartsTab from "./charts/MedicalChartsTab";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import BlurLoadingOverlay from "../common/BlurLoadingOverlay";
import { Client } from "../../types/client";
import { computeClientDetailStats } from "../../utils/clientDetailStats";
import { isClinicalVertical } from "../../utils/verticalCapabilities";
import { BusinessInfo, SalonContextType } from "../../types/context";
import styles from "./ClientDetails.module.css";

export type ClientDetailTab =
  | "overview"
  | "payment"
  | "packages"
  | "credits"
  | "notes"
  | "formulations"
  | "charts"
  | "documents"
  | "forms"
  | "soap-notes"
  | "activity";

interface ClientDetailsProps {
  client: Client;
  onClose: () => void;
  hideDeleteButton?: boolean;
  /** Tab to open on (defaults to "overview"). */
  initialTab?: ClientDetailTab;
  /**
   * Open a transaction's detail modal by UUID from the Activity Timeline.
   * Provided by the calendar drawer so the calendar opens its OWN full-screen
   * modal over the drawer. When omitted (the /admin/clients/:id page), this
   * component opens the modal in place.
   */
  onOpenTransaction?: (transactionId: string) => void;
}

const ClientDetails = ({
  client,
  onClose,
  hideDeleteButton = false,
  initialTab = "overview",
  onOpenTransaction,
}: ClientDetailsProps) => {
  const { deleteClient } = useClient();
  const { getClient, getClientLoyalty, getClientActivity, getTransaction } =
    useApp();
  const { businessInfo } = useSalon() as SalonContextType;
  const timezone =
    (businessInfo as BusinessInfo | null)?.timezone || "America/New_York";

  const perms = usePermissions();
  const clientPerms = perms?.clients;
  const canEditClients = clientPerms?.canView !== false;
  // Backend gates PHI workflows (forms + documents) by business_vertical via
  // declarative `required_business_vertical` on the workflow schema; when a
  // salon doesn't qualify, /access flips every canX flag to false. So the
  // canX flags are the single signal — no separate vertical check needed,
  // and any future expansion of PHI to other verticals is a backend-only
  // change.
  const formsPerms = perms?.formsAndWaivers;
  const canViewClientForms = formsPerms?.canViewClientForms === true;
  const canManageClientForms = formsPerms?.canManageClientForms === true;
  const documentsPerms = perms?.salonDocuments;
  const canViewClientDocuments = documentsPerms?.canViewDocuments === true;
  const canManageClientDocuments = documentsPerms?.canManageDocuments === true;

  const { businessVertical, access } = useFeatureAccess();
  const navigate = useNavigate();
  const messaging = useOptionalMessaging();
  // Clinical records (Medical Charts + SOAP notes) stay restricted to the
  // clinical verticals even though Forms & Waivers are open to every vertical.
  // `isClinicalVertical` is the single source of truth for that carve-out and
  // mirrors the backend's `clinical_enabled_for` allow-list.
  //
  // The vertical check is necessary but NOT sufficient: it is flag-independent,
  // so on its own it would light these tabs up for aesthetician salons the
  // moment this ships, while the backend still answers 403 until the
  // MedSpaFeaturesAllVerticals rollout flag flips. Each tab is therefore ANDed
  // with the section permission that backs its data, so the tab appears only
  // once the backend will actually serve it. Charts render saved salon
  // documents (`getSalonDocumentDetail`), so they follow `salonDocuments`;
  // SOAP notes follow `formsAndWaivers`.
  const showClinicalTabs = isClinicalVertical(businessVertical);
  const showChartsTab = showClinicalTabs && canViewClientDocuments;
  const showSoapNotesTab = showClinicalTabs && canViewClientForms;
  // Formulations (hair-color/processing records) are only meaningful for
  // hair salons; hide the tab for every other vertical.
  const showFormulationsTab = businessVertical === "hair";
  // Gift Cards (Credits tab) ships dark behind an env flag — hidden entirely
  // until it's flipped on. Mutating actions inside are further permission-gated.
  const giftCardsEnabled = process.env.REACT_APP_GIFT_CARDS_ENABLED === "true";

  const [detailClient, setDetailClient] = useState<Client>(client);
  const [detailLoading, setDetailLoading] = useState(false);
  /** Bumped when charts change (e.g. chart delete) so Documents tab can refetch its list. */
  const [documentsListInvalidateKey, setDocumentsListInvalidateKey] =
    useState(0);
  /** Bumped when client documents change (e.g. file delete) so Medical Charts can refetch its list. */
  const [chartsListInvalidateKey, setChartsListInvalidateKey] = useState(0);
  const [activeTab, setActiveTab] = useState<ClientDetailTab>(initialTab);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Loyalty (FE1 §4.1): points balance + Enrolled badge from
  // GET /clients/{id}/loyalty, only when the location's program is enabled.
  // Client-loyalty endpoints take the NUMERIC business_locations.id
  // (apiBusinessLocationId), not the salon uuid.
  const {
    isEnabled: loyaltyEnabled,
    apiBusinessLocationId: loyaltyLocationId,
  } = useLoyaltyProgram();
  const loyaltyActive = loyaltyEnabled && !!loyaltyLocationId;
  const [clientLoyalty, setClientLoyalty] =
    useState<ClientLoyaltyResponse | null>(null);
  const [isEditPointsOpen, setIsEditPointsOpen] = useState(false);

  const refreshClientLoyalty = useCallback(async () => {
    if (!loyaltyActive || !client?.id || !loyaltyLocationId) {
      setClientLoyalty(null);
      return;
    }
    try {
      const response = await getClientLoyalty(client.id, loyaltyLocationId);
      setClientLoyalty(response ?? null);
    } catch (e) {
      // Backend PR5 not deployed yet — treat as "no account" and keep the
      // drawer functional.
      console.error("Error loading client loyalty:", e);
      setClientLoyalty(null);
    }
  }, [loyaltyActive, client?.id, loyaltyLocationId, getClientLoyalty]);

  useEffect(() => {
    refreshClientLoyalty();
  }, [refreshClientLoyalty]);

  const loyaltyAccount = clientLoyalty?.account ?? null;

  // Activity Timeline (dedicated endpoint): eager-prefetched on mount, in
  // parallel with getClient + loyalty, so switching to the Activity tab shows
  // data with no fetch on tab change. Server-paginated (25/page) — "See More"
  // pulls the next cursor page and appends.
  const salonId = (businessInfo as BusinessInfo | null)?.id || "";
  const [activityEvents, setActivityEvents] = useState<ActivityEvent[]>([]);
  const [activityCursor, setActivityCursor] = useState<string | null>(null);
  // Start true so the timeline shows its spinner (not the "no activity" empty
  // state) on the first render, before the mount effect fires the fetch.
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityLoadingMore, setActivityLoadingMore] = useState(false);
  // Monotonic token guarding against out-of-order responses: switching the
  // client (the drawer reuses one ClientDetails instance) starts a new fetch,
  // and a slower in-flight response for the prior client must not overwrite it.
  const activityRequestId = useRef(0);

  const loadClientActivity = useCallback(async () => {
    const requestId = ++activityRequestId.current;
    if (!salonId || !client?.id) {
      setActivityEvents([]);
      setActivityCursor(null);
      setActivityLoading(false);
      return;
    }
    setActivityLoading(true);
    try {
      const res = await getClientActivity(salonId, client.id);
      if (requestId !== activityRequestId.current) return;
      setActivityEvents(res.events);
      setActivityCursor(res.next_cursor);
    } catch (e) {
      if (requestId !== activityRequestId.current) return;
      console.error("Error loading client activity:", e);
      setActivityEvents([]);
      setActivityCursor(null);
    } finally {
      if (requestId === activityRequestId.current) setActivityLoading(false);
    }
  }, [salonId, client?.id, getClientActivity]);

  useEffect(() => {
    loadClientActivity();
  }, [loadClientActivity]);

  const loadMoreActivity = useCallback(async () => {
    if (!salonId || !client?.id || !activityCursor) return;
    const requestId = activityRequestId.current;
    setActivityLoadingMore(true);
    try {
      const res = await getClientActivity(salonId, client.id, activityCursor);
      // Drop the page if the base list was reloaded (e.g. client switched)
      // while this "See More" fetch was in flight.
      if (requestId !== activityRequestId.current) return;
      setActivityEvents((prev) => [...prev, ...res.events]);
      setActivityCursor(res.next_cursor);
    } catch (e) {
      if (requestId !== activityRequestId.current) return;
      console.error("Error loading more client activity:", e);
    } finally {
      if (requestId === activityRequestId.current) {
        setActivityLoadingMore(false);
      }
    }
  }, [salonId, client?.id, activityCursor, getClientActivity]);

  // Transaction detail modal, opened in place from a timeline "Transaction #"
  // link. Only used on the full client page; the drawer delegates to the
  // calendar's own modal via onOpenTransaction (see handleOpenTransaction).
  const [txnModalTransaction, setTxnModalTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [txnModalOpen, setTxnModalOpen] = useState(false);
  const [txnModalLoading, setTxnModalLoading] = useState(false);

  const openTransactionInPage = useCallback(
    async (transactionId: string) => {
      // Blur the page with the standard spinner while the full transaction
      // loads, then open the modal once it is ready.
      setTxnModalLoading(true);
      try {
        const txn = await getTransaction(transactionId);
        if (txn) {
          setTxnModalTransaction(txn);
          setTxnModalOpen(true);
        }
      } catch (e) {
        console.error("Error loading transaction:", e);
      } finally {
        setTxnModalLoading(false);
      }
    },
    [getTransaction],
  );

  // Drawer (calendar) passes its own handler; the page falls back to in-place.
  const handleOpenTransaction = onOpenTransaction ?? openTransactionInPage;

  useEffect(() => {
    setDetailClient(client);
    // Land on the requested tab each time the drawer opens for a client; the
    // drawer instance is reused (client swapped in place), so reset here.
    setActiveTab(initialTab);
  }, [client, initialTab]);

  const refreshDetailClient = useCallback(async () => {
    if (!client?.id) return;
    setDetailLoading(true);
    try {
      const clientResponse = await getClient(client.id);
      const data = clientResponse?.clients?.[0];
      if (data) {
        setDetailClient(data);
      }
    } catch (e) {
      console.error("Error loading client detail:", e);
    } finally {
      setDetailLoading(false);
    }
  }, [client?.id, getClient]);

  useEffect(() => {
    refreshDetailClient();
  }, [refreshDetailClient]);

  const stats = useMemo(
    () =>
      computeClientDetailStats(
        detailClient.transactions,
        detailClient.last_appointment,
        timezone,
      ),
    [detailClient.transactions, detailClient.last_appointment, timezone],
  );

  const handleDeleteConfirm = async () => {
    try {
      await deleteClient(detailClient.id);
      onClose();
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  const notifyDocumentsListMayBeStale = useCallback(() => {
    setDocumentsListInvalidateKey((v) => v + 1);
  }, []);

  const notifyChartsListMayBeStale = useCallback(() => {
    setChartsListInvalidateKey((v) => v + 1);
  }, []);

  const tabIconProps = { size: 18, stroke: 1.75 } as const;

  // Two-way SMS entry point: same gate as the Chat nav (flag + Twilio platform +
  // messaging.canSend) and only when the client has a phone. Starting the chat is
  // idempotent (get-or-create) and links this client up front.
  const clientPhoneE164 = toE164(
    `${detailClient.area_code ?? ""}${detailClient.phone_number ?? ""}`,
  );
  const canMessageClient =
    isMessagingEnabled() &&
    access?.messaging_platform === "twilio" &&
    access?.section_permissions?.messaging?.canSend === true &&
    !!clientPhoneE164;

  const handleMessageClient = async () => {
    if (!messaging || !clientPhoneE164) return;
    try {
      const conversation = await messaging.createConversation(
        clientPhoneE164,
        detailClient.id,
      );
      navigate(`/admin/chat/${conversation.conversation_id}`);
    } catch (error) {
      console.error("Error starting chat with client:", error);
    }
  };

  return (
    <Stack gap="lg" className={styles.root}>
      <BrandedLoadingOverlay visible={detailLoading} />

      <ClientProfileHeader
        client={detailClient}
        canEdit={canEditClients}
        canDelete={!hideDeleteButton && clientPerms?.canDelete !== false}
        onEditProfile={() => setIsEditModalOpen(true)}
        onDeleteClient={() => setShowDeleteConfirmation(true)}
        canMessage={canMessageClient}
        onMessage={handleMessageClient}
        loyalty={
          loyaltyActive
            ? {
                enrolled: loyaltyAccount?.opted_in === true,
                pointsBalance: loyaltyAccount?.points_balance ?? 0,
                onEditPoints: () => setIsEditPointsOpen(true),
              }
            : null
        }
      />

      <Tabs
        value={activeTab}
        onChange={(v) => v && setActiveTab(v as ClientDetailTab)}
        variant="unstyled"
        classNames={{
          list: styles.tabList,
          tab: styles.tab,
          panel: styles.tabPanel,
        }}
      >
        <Tabs.List grow={false}>
          <Tabs.Tab
            value="overview"
            leftSection={<IconUser {...tabIconProps} />}
          >
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value="payment"
            leftSection={<IconCreditCard {...tabIconProps} />}
          >
            Payment Methods
          </Tabs.Tab>
          <Tabs.Tab
            value="packages"
            leftSection={<IconPackage {...tabIconProps} />}
          >
            Packages
          </Tabs.Tab>
          {giftCardsEnabled && (
            <Tabs.Tab
              value="credits"
              leftSection={<IconGift {...tabIconProps} />}
            >
              Credits
            </Tabs.Tab>
          )}
          <Tabs.Tab value="notes" leftSection={<IconNote {...tabIconProps} />}>
            Notes
          </Tabs.Tab>
          {showFormulationsTab && (
            <Tabs.Tab
              value="formulations"
              leftSection={<IconFlask {...tabIconProps} />}
            >
              Formulations
            </Tabs.Tab>
          )}
          {showChartsTab && (
            <Tabs.Tab
              value="charts"
              leftSection={<IconStethoscope {...tabIconProps} />}
            >
              Medical Charts
            </Tabs.Tab>
          )}
          {showSoapNotesTab && (
            <Tabs.Tab
              value="soap-notes"
              leftSection={<IconReportMedical {...tabIconProps} />}
            >
              SOAP Notes
            </Tabs.Tab>
          )}
          {canViewClientDocuments && (
            <Tabs.Tab
              value="documents"
              leftSection={<IconFileText {...tabIconProps} />}
            >
              Documents
            </Tabs.Tab>
          )}
          {canViewClientForms && (
            <Tabs.Tab
              value="forms"
              leftSection={<IconClipboardList {...tabIconProps} />}
            >
              Forms &amp; Waivers
            </Tabs.Tab>
          )}
          <Tabs.Tab
            value="activity"
            leftSection={<IconClock {...tabIconProps} />}
          >
            Activity Timeline
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview">
          <ClientOverviewPanel
            client={detailClient}
            stats={stats}
            timezone={timezone}
            loyalty={
              loyaltyActive
                ? {
                    pointsBalance: loyaltyAccount?.points_balance ?? 0,
                    lifetimePoints: loyaltyAccount?.lifetime_points ?? null,
                  }
                : null
            }
          />
        </Tabs.Panel>

        <Tabs.Panel value="payment">
          <div className={styles.sectionShell}>
            <PaymentMethodsSection
              clientId={detailClient.id}
              clientName={`${detailClient.first_name} ${detailClient.last_name}`}
              readOnly={!canEditClients}
              sectionTitle="Payment Methods"
              sectionSubtitle="Manage credit cards for quick checkout and deposits."
            />
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="packages">
          <div className={styles.sectionShell}>
            {/*
             * Remount per client. This section fetches via useSalonScopedFetch,
             * which fires each `${salonId}:${clientId}` key at most once for the
             * component's lifetime. This tree stays mounted while the client
             * swaps (route param change / drawer reuse), so without a fresh
             * mount a revisited client's tab keeps stale data. Keying on the
             * client id gives a fresh fetch guard + cleared local state, while
             * preserving the hook's within-client request-storm protection.
             */}
            <ClientPackagesSection
              key={detailClient.id}
              clientId={detailClient.id}
              readOnly={!canEditClients}
              sectionTitle="Service Packages"
              sectionSubtitle="Prepaid bundles owned by this client and their remaining balances."
            />
          </div>
        </Tabs.Panel>

        {giftCardsEnabled && (
          <Tabs.Panel value="credits">
            <div className={styles.sectionShell}>
              {/* Remount per client — see the Packages panel above for why. */}
              <ClientGiftCardsSection
                key={detailClient.id}
                clientId={detailClient.id}
                sectionTitle="Gift Cards"
                sectionSubtitle="Stored-value gift cards this client holds or has purchased."
              />
            </div>
          </Tabs.Panel>
        )}

        <Tabs.Panel value="notes">
          <ClientNotesPanel
            client={detailClient}
            type="note"
            onRequestRefresh={refreshDetailClient}
          />
        </Tabs.Panel>

        {showFormulationsTab && (
          <Tabs.Panel value="formulations">
            <ClientNotesPanel
              client={detailClient}
              type="formulation"
              onRequestRefresh={refreshDetailClient}
            />
          </Tabs.Panel>
        )}

        {showChartsTab && (
          <Tabs.Panel value="charts">
            <MedicalChartsTab
              clientId={client?.id}
              chartsListInvalidateKey={chartsListInvalidateKey}
              onChartDeleted={notifyDocumentsListMayBeStale}
            />
          </Tabs.Panel>
        )}

        {showSoapNotesTab && (
          <Tabs.Panel value="soap-notes">
            <ClientSoapNotesTab
              salonId={(businessInfo as BusinessInfo | null)?.id || ""}
              clientId={detailClient.id}
              readOnly={!canManageClientForms}
            />
          </Tabs.Panel>
        )}

        {canViewClientDocuments && (
          <Tabs.Panel value="documents">
            <ClientDocumentsTab
              salonId={(businessInfo as BusinessInfo | null)?.id || ""}
              clientId={detailClient.id}
              clientDisplayName={`${detailClient.first_name} ${detailClient.last_name}`.trim()}
              readOnly={!canManageClientDocuments}
              documentsListInvalidateKey={documentsListInvalidateKey}
              onClientDocumentDeleted={notifyChartsListMayBeStale}
            />
          </Tabs.Panel>
        )}

        {canViewClientForms && (
          <Tabs.Panel value="forms">
            <ClientFormsWaiversTab
              salonId={(businessInfo as BusinessInfo | null)?.id || ""}
              clientId={detailClient.id}
              clientDisplayName={`${detailClient.first_name} ${detailClient.last_name}`.trim()}
              readOnly={!canManageClientForms}
              clientPhone={detailClient.phone_number}
              clientOptedInReminder={detailClient.is_opted_in_reminder}
            />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="activity">
          <ActivityTimeline
            events={activityEvents}
            nextCursor={activityCursor}
            onLoadMore={loadMoreActivity}
            loading={activityLoading}
            loadingMore={activityLoadingMore}
            showLoyaltyPoints={loyaltyActive}
            thisClientId={detailClient.id}
            onOpenTransaction={handleOpenTransaction}
          />
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Client"
        size="lg"
      >
        <ClientForm
          client={detailClient}
          onClose={() => {
            setIsEditModalOpen(false);
            refreshDetailClient();
            // The Edit Client modal can change loyalty enrollment (§4.3).
            refreshClientLoyalty();
          }}
          readOnly={!canEditClients}
        />
      </Modal>

      {loyaltyActive && loyaltyLocationId && (
        <EditPointsModal
          opened={isEditPointsOpen}
          onClose={() => setIsEditPointsOpen(false)}
          clientId={detailClient.id}
          businessLocationId={loyaltyLocationId}
          currentPoints={loyaltyAccount?.points_balance ?? 0}
          onSaved={(response) => {
            // POST /adjust returns the updated account + new ledger row;
            // refresh the block from the response when possible.
            if (response?.account) {
              setClientLoyalty((prev) => ({
                ...(prev ?? {}),
                account: response.account,
              }));
            } else {
              refreshClientLoyalty();
            }
          }}
        />
      )}

      {!hideDeleteButton && (
        <DeleteConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Client"
          message={`Are you sure you want to delete ${detailClient.first_name} ${detailClient.last_name}? This action cannot be undone.`}
        />
      )}

      {/* Blurred page + standard spinner while a clicked transaction loads,
          before its detail modal opens (page context only). */}
      <BlurLoadingOverlay visible={!onOpenTransaction && txnModalLoading} />

      {/* In-place transaction detail modal — page context only, mounted only
          while open (it consumes TransactionProvider). The drawer delegates to
          the calendar's own modal via onOpenTransaction. */}
      {!onOpenTransaction && txnModalOpen && (
        <TransactionDetailModal
          opened={txnModalOpen}
          transaction={txnModalTransaction}
          isLoadingNewTransaction={false}
          onClose={() => {
            setTxnModalOpen(false);
            setTxnModalTransaction(null);
          }}
          onTransactionUpdated={(updated) => {
            setTxnModalTransaction(updated as GetTransactionsResponse);
            // Reflect any edit (refund, tip, void) back in the timeline.
            loadClientActivity();
          }}
        />
      )}
    </Stack>
  );
};

export default ClientDetails;
